import React, { useState, useEffect } from 'react';
import { Table, Project } from '../../types/domain';
import { apiClient } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { PageContainer } from '../layout/PageContainer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Save, Loader2, ShieldCheck, Zap } from 'lucide-react';

interface SpamProtectionTabProps {
  table: Table;
  project: Project;
  canEdit: boolean;
  onTableUpdated: (table: Table) => void;
}

export const SpamProtectionTab: React.FC<SpamProtectionTabProps> = ({
  table,
  project,
  canEdit,
  onTableUpdated,
}) => {
  const { showToast } = useToast();

  const [allowedOrigins, setAllowedOrigins] = useState(
    table.allowed_origins?.join(', ') || ''
  );
  const [honeypotField, setHoneypotField] = useState(table.honeypot_field || '_gotcha');
  const [secondaryHoneypots, setSecondaryHoneypots] = useState(
    table.honeypot_fields?.join(', ') || '_hp'
  );
  const [rateLimitRpm, setRateLimitRpm] = useState(String(table.rate_limit_rpm || 600));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAllowedOrigins(table.allowed_origins?.join(', ') || '');
    setHoneypotField(table.honeypot_field || '_gotcha');
    setSecondaryHoneypots(table.honeypot_fields?.join(', ') || '_hp');
    setRateLimitRpm(String(table.rate_limit_rpm || 600));
  }, [table.id, table.allowed_origins, table.honeypot_field, table.honeypot_fields, table.rate_limit_rpm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    setIsSaving(true);
    try {
      const originsArray = allowedOrigins
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);

      const extraHoneypots = secondaryHoneypots
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);

      const res = await apiClient<any>(
        `/projects/${project.id}/tables/${table.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            allowed_origins: originsArray,
            honeypot_field: honeypotField.trim(),
            honeypot_fields: extraHoneypots,
            rate_limit_rpm: parseInt(rateLimitRpm, 10) || 600,
          }),
        }
      );

      const updated = res.table || res || {
        ...table,
        allowed_origins: originsArray,
        honeypot_field: honeypotField.trim(),
        honeypot_fields: extraHoneypots,
        rate_limit_rpm: parseInt(rateLimitRpm, 10) || 600,
      };

      showToast({
        type: 'success',
        title: 'Spam Protection Saved',
        message: `Ingestion security and spam traps for '${table.name}' updated successfully.`,
      });

      onTableUpdated(updated);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to save spam protection',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer variant="tab">
      <form onSubmit={handleSubmit} className="space-y-8 text-xs">
        {/* Ingestion Security & Redirects */}
        <div className="p-5 rounded-lg bg-cf-surface border border-cf-border space-y-4">
          <h3 className="font-semibold text-cf-textPrimary text-xs uppercase tracking-wide flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-cf-orange" />
            <span>Spam Protection &amp; Ingress Guardrails</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Allowed Hostnames / Origins"
                type="text"
                disabled={!canEdit}
                value={allowedOrigins}
                onChange={(e) => setAllowedOrigins(e.target.value)}
                placeholder="example.com, https://app.example.com, *.example.com"
                helperText="Comma-separated hostnames or origins. If set, only requests from these hosts are accepted. Leave empty to allow all."
              />
            </div>

            <div>
              <Input
                label="Rate Limit (RPM)"
                type="number"
                disabled={!canEdit}
                value={rateLimitRpm}
                onChange={(e) => setRateLimitRpm(e.target.value)}
                placeholder="600"
                helperText="Max submissions per minute accepted from a single client IP."
              />
            </div>

            <div>
              <Input
                label="Primary Honeypot Field Name"
                type="text"
                disabled={!canEdit}
                value={honeypotField}
                onChange={(e) => setHoneypotField(e.target.value)}
                placeholder="_gotcha"
                helperText="Hidden field name. If populated, submission is quarantined silently without alerting the bot."
              />
            </div>

            <div>
              <Input
                label="Secondary Honeypot Fields (Comma-separated)"
                type="text"
                disabled={!canEdit}
                value={secondaryHoneypots}
                onChange={(e) => setSecondaryHoneypots(e.target.value)}
                placeholder="_hp, _secret_trap"
                helperText="Additional anti-spam honeypot inputs for multi-form configurations."
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        {canEdit && (
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              variant="primary"
              size="md"
              isLoading={isSaving}
              disabled={isSaving}
              leftIcon={<Save size={14} />}
            >
              Save Anti-Spam Guardrails
            </Button>
          </div>
        )}
      </form>
    </PageContainer>
  );
};
